import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { Shield, FileText, Cookie, AlertTriangle } from "lucide-react";

const SECTIONS = [
  { id: "vilkar",     icon: FileText },
  { id: "personvern", icon: Shield },
  { id: "innhold",    icon: AlertTriangle },
  { id: "cookies",    icon: Cookie },
];

const PATH_TO_SECTION = {
  "/vilkar": "vilkar",
  "/cookies": "cookies",
};

const SECTION_LABEL_KEYS = {
  vilkar: "personvern.title_terms",
  personvern: "personvern.title_privacy",
  innhold: "husregler.title",
  cookies: "personvern.title_cookies",
};

// Hjelpere for å rendre rik HTML fra i18n-nøkler (vi eier kildene)
const Html = ({ as: As = "p", html, className }) => (
  <As className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

export default function Personvern() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const hashTarget = window.location.hash ? window.location.hash.slice(1) : null;
    const pathTarget = PATH_TO_SECTION[location.pathname] || null;
    const target = hashTarget || pathTarget;
    if (target) {
      const el = document.getElementById(target);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.pathname]);

  const isNorwegian = i18n.language === "no";
  const k = (key) => t(`personvern.${key}`);

  const H3 = ({ children }) => (
    <h3 className="font-meta text-[var(--cream)] mb-2 mt-8">{children}</h3>
  );
  const P = ({ html, plain, className = "text-[var(--cream-soft)] leading-relaxed mb-4" }) =>
    html ? <Html html={html} className={className} /> : <p className={className}>{plain}</p>;
  const UL = ({ children }) => (
    <ul className="list-disc pl-5 text-[var(--cream-soft)] space-y-2 mb-4 leading-relaxed">{children}</ul>
  );
  const LI = ({ html }) => <li dangerouslySetInnerHTML={{ __html: html }} />;

  return (
    <Layout withSidebar={false}>
      <div className="max-w-3xl mx-auto" data-testid="personvern-page">
        <header className="mb-12">
          <div className="font-meta text-[var(--magenta)] mb-3">{t("personvern.section_label")}</div>
          <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] leading-tight mb-4">
            {t("personvern.title_terms").replace(".", "")} & {t("personvern.title_privacy").replace(".", "")}.
          </h1>
          <p className="text-[var(--cream-soft)] leading-relaxed">
            {t("personvern.intro_terms")} {t("personvern.intro_privacy")}
          </p>
          <div className="font-meta text-[var(--cream-faint)] mt-5">
            {t("personvern.last_updated")}
          </div>
          {!isNorwegian && (
            <div className="mt-6 border-l-2 border-[var(--gold)] bg-[var(--gold)]/5 p-4">
              <div className="font-meta text-[var(--gold)] mb-1">⚠ NOTICE</div>
              <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed">
                {t("personvern.translation_notice")}
              </p>
            </div>
          )}
        </header>

        <nav className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-12 border-y border-white/10 py-5" data-testid="personvern-toc">
          {SECTIONS.map(({ id, icon: Icon }) => (
            <a
              key={id}
              href={`#${id}`}
              className="flex items-center gap-2 px-3 py-2 font-meta text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
              data-testid={`toc-${id}`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t(SECTION_LABEL_KEYS[id])}</span>
            </a>
          ))}
        </nav>

        {/* === BRUKERVILKÅR === */}
        <section id="vilkar" className="mb-16 scroll-mt-24">
          <h2 className="font-fraunces italic text-3xl text-[var(--magenta)] mb-6">{k("h2_terms")}</h2>

          <H3>{k("t1_h")}</H3>
          <P html={k("t1_p")} />

          <H3>{k("t2_h")}</H3>
          <UL>
            <LI html={k("t2_b1")} />
            <LI html={k("t2_b2")} />
            <LI html={k("t2_b3")} />
            <LI html={k("t2_b4")} />
          </UL>

          <H3>{k("t3_h")}</H3>
          <P html={k("t3_intro")} className="text-[var(--cream-soft)] leading-relaxed mb-3" />
          <UL>
            {[1,2,3,4,5,6,7,8,9,10,11,12,13].map(n => (
              <LI key={n} html={k(`t3_b${n}`)} />
            ))}
          </UL>
          <P html={k("t3_outro")} />

          <H3>{k("t4_h")}</H3>
          <P html={k("t4_p")} />

          <H3>{k("t5_h")}</H3>
          <P html={k("t5_p")} className="text-[var(--cream-soft)] leading-relaxed" />
        </section>

        {/* === INNHOLD & SAMTYKKE === */}
        <section id="innhold" className="mb-16 scroll-mt-24">
          <h2 className="font-fraunces italic text-3xl text-[var(--magenta)] mb-6">{k("h2_content")}</h2>

          <H3>{k("c1_h")}</H3>
          <P html={k("c1_intro")} className="text-[var(--cream-soft)] leading-relaxed mb-3" />
          <UL>
            <LI html={k("c1_b1")} />
            <LI html={k("c1_b2")} />
            <LI html={k("c1_b3")} />
            <LI html={k("c1_b4")} />
          </UL>

          <H3>{k("c2_h")}</H3>
          <P html={k("c2_p")} />

          <H3>{k("c3_h")}</H3>
          <P html={k("c3_p")} />

          <H3>{k("c4_h")}</H3>
          <P html={k("c4_p")} className="text-[var(--cream-soft)] leading-relaxed" />
        </section>

        {/* === PERSONVERN === */}
        <section id="personvern" className="mb-16 scroll-mt-24">
          <h2 className="font-fraunces italic text-3xl text-[var(--magenta)] mb-6">{k("h2_privacy")}</h2>

          <H3>{k("p1_h")}</H3>
          <P html={k("p1_p")} />

          <H3>{k("p2_h")}</H3>
          <UL>
            {[1,2,3,4,5].map(n => <LI key={n} html={k(`p2_b${n}`)} />)}
          </UL>

          <H3>{k("p3_h")}</H3>
          <P html={k("p3_p")} />

          <H3>{k("p4_h")}</H3>
          <UL>
            {[1,2,3,4,5].map(n => <LI key={n} html={k(`p4_b${n}`)} />)}
          </UL>

          <H3>{k("p5_h")}</H3>
          <UL>
            {[1,2,3].map(n => <LI key={n} html={k(`p5_b${n}`)} />)}
          </UL>

          <H3>{k("p6_h")}</H3>
          <P html={k("p6_p1")} className="text-[var(--cream-soft)] leading-relaxed mb-3" />
          <P html={k("p6_p2")} className="text-[var(--cream-soft)] leading-relaxed mb-3" />
          <UL>
            {[1,2,3].map(n => <LI key={n} html={k(`p6_b${n}`)} />)}
          </UL>
          <P html={k("p6_p3")} className="text-[var(--cream-soft)] leading-relaxed" />

          <H3>{k("p7_h")}</H3>
          <UL>
            {[1,2,3,4,5,6,7].map(n => <LI key={n} html={k(`p7_b${n}`)} />)}
          </UL>

          <H3>{k("p8_h")}</H3>
          <P html={k("p8_intro")} className="text-[var(--cream-soft)] leading-relaxed mb-3" />
          <UL>
            {[1,2,3,4,5,6,7].map(n => <LI key={n} html={k(`p8_b${n}`)} />)}
          </UL>
          <P html={k("p8_outro1")} className="text-[var(--cream-soft)] leading-relaxed" />
          <P html={k("p8_outro2")} className="text-[var(--cream-soft)] leading-relaxed mt-3" />

          <H3>{k("p9_h")}</H3>
          <ul className="list-disc pl-5 text-[var(--cream-soft)] space-y-2 leading-relaxed">
            {[1,2,3,4,5].map(n => <LI key={n} html={k(`p9_b${n}`)} />)}
          </ul>
        </section>

        {/* === COOKIES === */}
        <section id="cookies" className="mb-16 scroll-mt-24">
          <h2 className="font-fraunces italic text-3xl text-[var(--magenta)] mb-6">{k("h2_cookies")}</h2>

          <P html={k("ck_intro")} />

          <H3>{k("ck1_h")}</H3>
          <UL>
            <LI html={k("ck1_b1")} />
            <LI html={k("ck1_b2")} />
            <LI html={k("ck1_b3")} />
          </UL>

          <H3>{k("ck2_h")}</H3>
          <ul className="list-disc pl-5 text-[var(--cream-soft)] space-y-2 leading-relaxed">
            <LI html={k("ck2_b1")} />
            <LI html={k("ck2_b2")} />
            <LI html={k("ck2_b3")} />
          </ul>
        </section>

        {/* Bunn-CTA */}
        <div className="border-t border-white/10 pt-10 mt-10">
          <P
            html={k("footer_q")}
            className="text-[var(--cream-soft)] text-sm leading-relaxed [&_a]:text-[var(--magenta)] [&_a]:underline-offset-4 hover:[&_a]:underline"
          />
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link to="/" className="btn-ghost">{k("to_home")}</Link>
            <Link to="/register" className="btn-magenta" data-testid="personvern-register-cta">{k("btn_join")}</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
